// useMicPermission.js
import { useState, useCallback } from "react";

/**
 * Returns:
 *   checkAndRequest() — call this before starting recording.
 *     Resolves to true if mic is available, false if denied/unavailable.
 *   permissionState — "unknown" | "granted" | "denied" | "prompt"
 */
export default function useMicPermission() {
    const [permissionState, setPermissionState] = useState("unknown");

    const checkAndRequest = useCallback(async () => {
        try {
            // Try the Permissions API first (non-blocking check)
            if (navigator.permissions) {
                const result = await navigator.permissions.query({ name: "microphone" });
                setPermissionState(result.state);
                if (result.state === "granted") return true;
                if (result.state === "denied") {
                    setPermissionState("denied");
                    return false;
                }
                // state === "prompt" — fall through to getUserMedia to trigger the dialog
            }

            // Trigger the browser's native permission dialog
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // Immediately stop the tracks — we only needed the permission grant
            stream.getTracks().forEach(t => t.stop());
            setPermissionState("granted");
            return true;
        } catch (err) {
            // NotAllowedError = user denied, NotFoundError = no mic hardware
            setPermissionState("denied");
            return false;
        }
    }, []);

    return { permissionState, checkAndRequest };
}